import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useLocation, useNavigate, useParams, useSearchParams, useBlocker, useHref, Navigate } from 'react-router-dom';
import { BETA, DEVELOPMENT, getEnvironmentType, isTest, STAGING } from '../environment';
import { FEATURE_FLAGS } from '../constants/feature-flags';
import {
  SINGLE_MODE,
  MINIMAL_UI,
  ORIGIN_ROUTE,
  DRILL_DOWN,
  DRILL_DOWN_STOP,
  SHOW_HIDDEN,
  MOCK_DATA,
  EMBED_MODE
} from '../constants/routes';

/** Preparation for softer migration to v6
 * for class components or functional components wrapped by redux connect or other primitive methods,
 * we need to pass router prop that will be used inside redux connect, recompose if needed (hooks can be used there directly)
 * so that selectors can use params (mainly) that will not be available in v6 */
export function withRouter(Component) {
  function ComponentWithRouterProp(props) {
    const location = useLocation();
    const params = useParams();
    const navigate = useNavigate();
    const advancedNavigate = useAdvancedNavigate();
    let basename = useHref('/');
    if (basename === '/') basename = ''; /** BASE_PATH (used in Router) in store.js removes trailing slash */
    const openHref = openHrefCallBack(basename);
    const [searchParams, setSearchParams] = useSearchParams();
    const [, advancedSetSearchParams] = useAdvancedSearchParams();
    return (
      <Component
        {...props}
        router={{
          location,
          params,
          navigate,
          advancedNavigate,
          searchParams,
          setSearchParams,
          advancedSetSearchParams,
          basename,
          openHref
        }}
      />
    );
  }

  return ComponentWithRouterProp;
}

export const getRouterParamValueFromProps = (props, paramName) =>
  props.router?.params?.[paramName] || props.match?.params?.[paramName];

export const useOpenHref = () => {
  let basename = useHref('/');
  if (basename === '/') basename = ''; /** BASE_PATH (used in Router) in store.js removes trailing slash */
  return openHrefCallBack(basename);
};

const openHrefCallBack = basename =>
  useCallback(
    (url, target = null) => {
      const prefix = typeof url === 'string' && url.startsWith('/') ? basename : '';
      window.open(`${prefix}${url}`, target);
    },
    [basename]
  );

export const usePrompt = ({ when, message }) => {
  const blocker = useBlocker(when);
  const basename = useHref('/');

  useEffect(() => {
    if (blocker.state === 'blocked') {
      let isProceed = !when;
      if (when) {
        if (typeof message === 'function') {
          const { pathname } = blocker.location;
          if (basename !== '/' && pathname.startsWith(basename)) {
            blocker.location.pathname = pathname.replace(basename, '');
          }
          const result = message(blocker.location);
          isProceed = typeof result === 'boolean' ? result : isProceed;
        } else if (typeof message === 'string') {
          // eslint-disable-next-line no-alert
          isProceed = window.confirm(message);
        }
      }

      if (isProceed) {
        setTimeout(blocker.proceed, 0); // proceed to next route
      } else {
        blocker.reset(); // don't proceed & keep blocking
      }
    }
  }, [blocker, message, when]);
};

const PromptInternal = ({ when, message }) => {
  usePrompt({ when, message });
  return null;
};
export const Prompt = ({ when, message }) => (isTest ? null : <PromptInternal when={when} message={message} />);

Prompt.propType = {
  when: PropTypes.bool,
  message: PropTypes.node
};

/** shouldBlock : bool || { currentLocation, nextLocation, historyAction }; */
export const useRouteBlocker = shouldBlock => {
  const blocker = useBlocker(isTest ? false : shouldBlock);
  const isBlocked = isTest ? false : blocker.state === 'blocked';
  const proceed = () => isBlocked && blocker.proceed?.();
  const stay = () => isBlocked && blocker.reset?.();

  return { isBlocked: blocker.state === 'blocked', proceed, stay };
};

const combineNavigateArguments = (to, options, location) => {
  const { replace, state, relative } = options || {};
  const { search, hash, state: _curState } = location;
  const newTo =
    typeof to === 'string'
      ? { pathname: to, search, hash }
      : { pathname: to.pathname, search: to.search || search, hash: to.hash || hash };
  const newOptions = { state: state || _curState, replace, relative };

  return { newTo, newOptions };
};

/** Advanced Navigate (Redirect) component that preserves previous search, hash and state */
export const AdvancedNavigate = ({ to, replace, state, relative }) => {
  const location = useLocation();
  const { newTo, newOptions } = combineNavigateArguments(to, { replace, state, relative }, location);
  return <Navigate to={newTo} state={newOptions.state} replace={newOptions.replace} relative={newOptions.relative} />;
};

AdvancedNavigate.propType = {
  to: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      pathname: PropTypes.string,
      search: PropTypes.string,
      hash: PropTypes.string
    })
  ]),
  replace: PropTypes.bool,
  relative: PropTypes.oneOf(['path', 'root']),
  state: PropTypes.object
};

const DEFAULT_ROUTE = 'default';
export const useAdvancedNavigate = () => {
  const location = useLocation();
  const navigate = useNavigate();
  return useCallback(
    (to, options) => {
      if (typeof to === 'number' && !options) {
        return location.key === DEFAULT_ROUTE && to < 0 ? navigate('..', { relative: 'path' }) : navigate(to);
      }
      const { newTo, newOptions } = combineNavigateArguments(to, options, location);
      return navigate(newTo, newOptions);
    },
    [navigate, location]
  );
};

export const useAdvancedSearchParams = () => {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  return [
    searchParams,
    useCallback(
      (newSearchParams, options) => {
        const { newOptions } = combineNavigateArguments('', options, location);
        return setSearchParams(newSearchParams, newOptions);
      },
      [setSearchParams, location]
    )
  ];
};

export const isSingleModeUi = () => {
  try {
    return !!window.argosParams?.[SINGLE_MODE];
  } catch {
    return false;
  }
};

export const isMinimalUi = () => !!window?.argosParams?.[MINIMAL_UI];
export const isMockData = () => !!window?.argosParams?.[MOCK_DATA];

export const isEmbedded = window.argosParams?.[EMBED_MODE] ?? window !== window.parent;

export const isShowHidden = () => {
  try {
    return !!window.argosParams?.[SHOW_HIDDEN];
  } catch {
    return false;
  }
};

export const isShowHiddenFeature = () => {
  if ([DEVELOPMENT, STAGING, BETA].includes(getEnvironmentType())) return true;
  return isShowHidden();
};

export const isLocalizationVisible = ({ flags } = {}) => {
  if (window.argosParams?.[SHOW_HIDDEN] === false) return false;
  return !!(flags || window.argosParams)?.[FEATURE_FLAGS.LANGUAGE_SWITCHER_VISIBILITY];
};

export const isAsmNgVisible = () => {
  if (window.argosParams?.[SHOW_HIDDEN] === false) return false; // to explicitly see old ASM
  return true;
};

export const isNotificationsVisible = () => {
  return window.argosParams?.[FEATURE_FLAGS.NC_CONFIG]?.isVisible;
};
export const isNotificationsManagerVisible = () => {
  return isNotificationsVisible() && window.argosParams?.[FEATURE_FLAGS.NC_CONFIG]?.showManager !== false;
};

export const isNotificationsAnnouncementVisible = () => {
  return isNotificationsVisible() && window.argosParams?.[FEATURE_FLAGS.NC_CONFIG]?.showAnnouncements !== false;
};
export const isNotificationsActivityVisible = () => {
  return isNotificationsVisible() && window.argosParams?.[FEATURE_FLAGS.NC_CONFIG]?.showActivities !== false;
};

export const getMatchRouteName = match => {
  const routeName = match?.handle?.routeName;
  if (typeof routeName === 'function') {
    return routeName({ match });
  }
  return routeName;
};

export const buildDrillDownState = state => ({
  ...state,
  [DRILL_DOWN]: true
});

export const buildDrillDownStopState = state => ({
  ...state,
  [DRILL_DOWN_STOP]: true
});

export const buildOriginRouteState = ({ matches }) =>
  matches
    ? {
        [ORIGIN_ROUTE]: matches[matches.length - 1]
      }
    : null;

export const getOriginRouteInfo = state => {
  const originRouteMatch = state?.[ORIGIN_ROUTE];
  return originRouteMatch
    ? {
        routeName: getMatchRouteName(originRouteMatch),
        pathname: originRouteMatch.pathname
      }
    : null;
};
